import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useState } from "react";
import { ObjectSchema, object } from "yup";
import { requiredString } from "../../../../data/yupConstants";
import {
  RightsHoldersNamesViewModel,
  SubmitPseudonymViewModel,
} from "../../../../generated/data-contracts";
import { ErrorUtils, api } from "../../../../utils";
import { AvisSection } from "../../../shared/components/form";
import { AvisConfirmModalShort } from "../../../shared/components/modal";
import { AvisContext } from "../../../shared/context/AvisContext";
import { SubmitNameForm } from "./SubmitNameForm";

interface ISubmitNameProps {
  rhNames?: RightsHoldersNamesViewModel[];
  onSubmit: () => void;
  loading: boolean;
}
export function SubmitName(props: Readonly<ISubmitNameProps>) {
  const { rhNames, onSubmit, loading } = props;
  const { ctxRightsHolder, res } = useContext(AvisContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const checkDuplicates = (name: string): boolean => {
    if (!rhNames) return true;

    let result = true;

    for (const rhname of rhNames) {
      if (
        rhname.lastName?.trim().toLocaleLowerCase() ===
        name.trim().toLocaleLowerCase()
      ) {
        result = false;
        break;
      }
    }

    return result;
  };

  const validationSchema: ObjectSchema<SubmitPseudonymViewModel> = object({
    pseudonym: requiredString(res)
      .min(2, res("authors_NamesPage_Pseudonym_validationMinLength", "2"))
      .max(100, res("authors_NamesPage_Pseudonym_validationMaxLength", "100"))
      .test(
        "check-duplicates",
        res("authors_NamesPage_Pseudonym_validationDuplicate"),
        checkDuplicates,
      ),
  });

  const handleSubmit = (
    values: SubmitPseudonymViewModel,
    formikHelpers: FormikHelpers<SubmitPseudonymViewModel>,
  ) => {
    if (!ctxRightsHolder?.rightsHolderId) return;

    setIsSubmitting(true);

    api
      .rightsHoldersPostRightsHoldersName(
        ctxRightsHolder.rightsHolderId,
        values,
      )
      .then(() => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("authors_NamesPage_Pseudonym_SuccessfullyAdded"),
        );
        formikHelpers.resetForm();
        onSubmit();
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitPseudonymViewModel>) => {
      return (
        <>
          <SubmitNameForm
            bag={formikProps}
            loading={isSubmitting || loading}
            showModal={() => setIsModalOpen(true)}
          />

          {/* temporary workaround for hide dots modal empty div */}
          {isModalOpen && (
            <AvisConfirmModalShort
              isOpen={isModalOpen}
              content={res("authors_NamesPage_Pseudonym_ConfirmAddNew")}
              confirm={formikProps.submitForm}
              cancel={() => setIsModalOpen(false)}
              title={res("title_Confirm")}
              disableControls={isSubmitting}
            />
          )}
        </>
      );
    },
    [isModalOpen, isSubmitting, res, loading],
  );

  return (
    <AvisSection title={res("authors_NamesPage_SectionNewTitle")}>
      <Formik<SubmitPseudonymViewModel>
        initialValues={{
          pseudonym: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {renderForm}
      </Formik>
    </AvisSection>
  );
}
